import { useEffect } from "react";

/**
 * iOS Safari, Android Chrome (and most mobile browsers) block `audio.play()`
 * unless it originates from a *synchronous* user-gesture handler. Our TTS
 * playback fires *after* an async `fetch()`, which loses the "user-gesture"
 * tag — so on mobile the audio is silently rejected.
 *
 * Fix: the first time the user taps anywhere, we synchronously play a *silent*
 * 1-byte audio element. From that point on, the browser treats this origin as
 * "audio is allowed", and subsequent `new Audio(...).play()` calls work.
 *
 * Idempotent — guarded by a window flag so multiple mounts don't re-attach.
 */
export default function useAudioUnlock() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.__sashaAudioUnlocked) return;

    // Inaudible 1-second MP3 (volume 0). Base64 lifted from the standard
    // "silent.mp3" trick used by HTML5 audio libraries.
    const SILENCE_MP3 =
      "data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBUcmltbWVkIGJ5IFNveCBmb3IgRGVtbwBUSVQyAAAAEgAAA0F0bW9zcGhlcmUgU291bmRzAFRSQ0sAAAAFAAADMS80AFRQRTEAAAAJAAADQXJ0aXN0AFRBTEIAAAAMAAADQWxidW0gTmFtZQBUWUVSAAAABAAAAzIwMTUA//uQwAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACgABXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1d/////////////////////////////////////////////////////////8AAAAATGF2YzU3LjEwAAAAAAAAAAAAAAAAJAYAAAAAAAAAACgAQApfPlYAAAAAAAAAAAAAAAA=";

    const unlock = () => {
      try {
        const silence = new Audio(SILENCE_MP3);
        silence.volume = 0;
        const p = silence.play();
        if (p && typeof p.then === "function") {
          p.then(() => {
            silence.pause();
            silence.currentTime = 0;
          }).catch(() => {});
        }
        // Also kick the AudioContext awake — some browsers gate that separately.
        try {
          const AC = window.AudioContext || window.webkitAudioContext;
          if (AC && !window.__sashaAudioCtx) {
            const ctx = new AC();
            window.__sashaAudioCtx = ctx;
            if (ctx.state === "suspended" && typeof ctx.resume === "function") {
              ctx.resume().catch(() => {});
            }
          }
        } catch {
          /* AudioContext not available */
        }
      } catch {
        /* swallow */
      }
      window.__sashaAudioUnlocked = true;
      removeListeners();
    };

    const opts = { capture: true, passive: true };
    const removeListeners = () => {
      document.removeEventListener("click", unlock, true);
      document.removeEventListener("touchstart", unlock, true);
      document.removeEventListener("keydown", unlock, true);
      document.removeEventListener("pointerdown", unlock, true);
    };
    document.addEventListener("click", unlock, opts);
    document.addEventListener("touchstart", unlock, opts);
    document.addEventListener("keydown", unlock, opts);
    document.addEventListener("pointerdown", unlock, opts);

    return removeListeners;
  }, []);
}
